import { forwardRef, useImperativeHandle, useReducer, useState } from "react";
import ReactDOM from "react-dom";
import IC_MODAL_CLOSE from "@/assets/simulInter/ic_modal_close.svg?react";
import deviceStore from '@/store/device.store';
import { message } from "antd";

interface CodeConnectModalProps {
  streamName: string;
  handleConnectByIp: () => void;
  onConnectSuccess?: (isStart: boolean, streamName?: string) => void;
  onClose?: (needToHome: boolean) => void; // 添加关闭回调
}

export interface CodeConnectModalRef {
  open: () => void;
  close: () => void;
}

enum ConnectorState {
  Disabled = "disabled",
  Ready = "ready",
  Connecting = "connecting",
  Error = "error",
}

interface ConnectState {
  status: ConnectorState;
  code: string;
  errorMsg: string;
}

const initialState: ConnectState = {
  status: ConnectorState.Disabled,
  code: "",
  errorMsg: "",
};

const connectReducer = (state: ConnectState, action: Partial<ConnectState>) => {
  return { ...state, ...action }
};

const CodeConnectModal = forwardRef<CodeConnectModalRef, CodeConnectModalProps>(
  (props, ref) => {
    const { handleConnectByIp, onConnectSuccess, onClose } = props;
    const [visible, setVisible] = useState(false);
    const [connectState, dispatch] = useReducer(connectReducer, initialState);
    const [isComposition, setIsComposition] = useState(false);

    const { status, code, errorMsg } = connectState;
    const allowLocalConnect = localStorage.getItem("allowLocalConnect") === "true";

    const handleClose = async (needToHome: boolean = false) => {
      setVisible(false);
      // 关闭时重置状态
      dispatch({
        code: "",
        status: ConnectorState.Disabled,
        errorMsg: "",
      });
      // 调用外部关闭回调
      onClose?.(needToHome);
    };

    const open = () => {
      setVisible(true);
    };

    const handleCompositionStart = () => {
      setIsComposition(true);
    };

    const handleCompositionEnd = (e: React.CompositionEvent<HTMLInputElement>) => {
      setIsComposition(false);
      const target = e.target as HTMLInputElement;
      innerChange(target.value);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      dispatch({ code: value });
      if (!isComposition) {
        innerChange(value);
      }
    };

    const innerChange = (value: string) => {
      const code = value.toUpperCase().replace(/[^a-zA-Z0-9]/g, "");
      if (code.length === 0) {
        dispatch({ code: "" });
        return;
      }
      const codeStatus =
        code.length === 6 || code.length === 8
          ? ConnectorState.Ready
          : ConnectorState.Disabled;
      dispatch({ status: codeStatus, code, errorMsg: "" });
    };

    const handleEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key !== "Enter") return;
      if (status === ConnectorState.Connecting) return;
      if (status === ConnectorState.Disabled) {
        console.log('输入格式不对，返回');
        return;
      }

      e.currentTarget.blur();
      handleConnect();
    };

    const handleConnectLocal = async () => {

      const result = await deviceStore.getState().connectDeviceByIP('127.0.0.1', '2998');
      if (result.success) {
        onConnectSuccess && onConnectSuccess(result.isRecording || false, result.streamName);
      } else {
        message.error(result.errorMsg || "连接失败");
      }
    };

    const handleConnect = async () => {
      if (status !== ConnectorState.Ready) return;
      dispatch({ status: ConnectorState.Connecting, errorMsg: "" });

      const result = await deviceStore.getState().connectDeviceByCode(code);
      if (result.success) {
        dispatch({ status: ConnectorState.Ready });
        onConnectSuccess && onConnectSuccess(result.isRecording || false, result.streamName);
      } else {
        dispatch({ status: ConnectorState.Error, errorMsg: result.errorMsg || "连接失败" });
      }
    };

    useImperativeHandle(ref, () => ({
      open,
      close: handleClose,
    }));


    return visible
      ? ReactDOM.createPortal(
        <div
          className={`fixed z-1 w-full h-full inset-0 flex items-center justify-center z-999 bg-black bg-opacity-50`}
        >
          <div className="flex flex-col items-center bg-white w-130 min-h-79.5 rounded-4 shadow relative overflow-hidden">
            <div
              className="absolute right-3 top-3 cursor-pointer"
              onClick={() => handleClose(true)}
            >
              <IC_MODAL_CLOSE className="w-8 h-8" />
            </div>
            <div className="mt-16 font-600 text-4.5 lh-7.5 text-mx-secondary">
              输入设备「连接码」，连接设备
            </div>
            <div className="mt-1 text-#626773CC/80">
              确保连接设备在同一局域网络下
            </div>

            {/* 连接码输入框 */}
            <input
              className={`mt-6 border-1px border-solid rounded-1.5 w-100 h-12 px-7.25 py-3 focus:outline-none lh-6 text-#30343C text-4 lh-6 text-center placeholder-#30343C/30 tracking-1.5 ${status === ConnectorState.Error ? "border-#F5222D" : "border-#DBDEE4"
                }`}
              value={code}
              onChange={handleChange}
              onCompositionStart={handleCompositionStart}
              onCompositionEnd={handleCompositionEnd}
              onKeyUp={handleEnter}
              placeholder="请输入"
              maxLength={8}
              spellCheck={false}
              autoFocus
            />

            {/* 错误提示 */}
            {status === ConnectorState.Error && (
              <div className="mt-2 text-center text-#FF584C text-3 line-height-4.25">
                {errorMsg}
              </div>
            )}

            {/* 连接按钮 */}
            <div
              className={`mb-15 mt-8 w-36.5 h-12 flex items-center justify-center rd-2.5 text-white text-3.5 font-500 ${status === ConnectorState.Disabled
                ? "bg-mx-primary/60 cursor-not-allowed"
                : status === ConnectorState.Connecting
                  ? "bg-mx-primary/80 cursor-not-allowed"
                  : status === ConnectorState.Error
                    ? "bg-mx-primary cursor-pointer"
                    : "bg-mx-primary cursor-pointer"
                }`}
              onClick={handleConnect}
            >
              {status === ConnectorState.Connecting ? "连接中..." : "连 接"}
            </div>

            {/* IP地址连接入口 */}
            <div className="mb-6 text-3 text-mx-primary lh-5 flex" onClick={handleConnectByIp}>
              <span className="cursor-pointer">使用IP地址连接</span>
              {allowLocalConnect && <span className="ml-2 cursor-pointer" onClick={handleConnectLocal}>本地连接</span>}
            </div>
          </div>
        </div>,
        document.body
      )
      : null;
  }
);

export default CodeConnectModal;
